/**
 * Which era of WoW a row belongs to.
 *
 * Rows across the database carry a `game_version`, and the values do not mean
 * what you would guess, so read this before changing anything here.
 *
 * The guild has played four rulesets: Turtle WoW, a WotLK server, octowow.st,
 * and now WoW Forever. But the tag does not line up one-to-one with them,
 * because octowow.st mirrors the original Turtle WoW database exactly:
 *
 *   - Shared CONTENT - raids, bosses, raid events, items, crafting spells -
 *     is tagged 'turtle_wow'. Those rows genuinely are the Turtle WoW ruleset
 *     that octowow.st reuses, so migration 100 deliberately kept the tag
 *     rather than duplicating every row under a new name.
 *
 *   - CHARACTERS are tagged 'octowow'. A character is a real thing on a real
 *     server, not shared ruleset data, so the old Turtle WoW and WotLK
 *     profiles were archived rather than carried over.
 *
 * Both are 'forever' as of the switchover (24 September 2026). Before it they
 * differed - 'turtle_wow' for content and 'octowow' for people at the same
 * time - which is why they are two constants and not one, and why a blanket
 * find-and-replace would have broken things: the same literal meant "current"
 * in one query and "history" in another. Anything reading history stays on
 * the LEGACY values below.
 *
 * The site was flipped while empty, deliberately: the guild is not playing
 * anything until Forever launches, so pages showing nothing are accurate
 * rather than broken. They fill as raids, bosses and items are added.
 */

export const GAME_VERSIONS = ['turtle_wow', 'wrath', 'octowow', 'forever'] as const

export type GameVersion = (typeof GAME_VERSIONS)[number]

/**
 * The ruleset being played: raids, bosses, raid events, loot items, crafting.
 * Not the same value as PROFILE_VERSION - see the note above.
 */
export const CONTENT_VERSION: GameVersion = 'forever'

/**
 * The server characters live on: `player_profiles`.
 */
export const PROFILE_VERSION: GameVersion = 'forever'

/**
 * The era members register characters for.
 *
 * Deliberately ahead of PROFILE_VERSION: people declare their Forever main
 * now, while the roster still shows the octowow characters they are actually
 * playing. When the guild moves, PROFILE_VERSION becomes this and the roster
 * switches over to everything registered in the meantime.
 */
export const REGISTRATION_VERSION: GameVersion = 'forever'

/**
 * Past eras. Only for looking backwards - the archive, and matching a member
 * to the character they used to play. Never use these to load current data.
 */
export const LEGACY = {
  TURTLE_WOW: 'turtle_wow',
  WRATH: 'wrath',
} as const satisfies Record<string, GameVersion>

/** How each era is written for a person to read. */
export const GAME_VERSION_LABELS: Record<GameVersion, string> = {
  turtle_wow: 'Turtle WoW',
  wrath: 'Wrath of the Lich King',
  octowow: 'octowow.st',
  forever: 'WoW Forever',
}

export function gameVersionLabel(version: string | null | undefined): string {
  if (!version) return 'Unknown'
  return GAME_VERSION_LABELS[version as GameVersion] ?? version
}
